import { SbJobType } from '../services/serviceBooking';
import { IService } from './entities/interface';

/**
 * Shared between ConvertLeadPanel.tsx (marketing leads) and
 * ConvertPartnerLeadPanel.tsx (channel-partner leads) — both convert a lead
 * into an Enquiry and both need a live "Enquiry Type" picker.
 *
 * CORRECTION: this originally read from the legacy `Service` collection
 * (services/service.ts#GETServices). That collection can carry two rows for
 * the same job — a hand-made legacy row, plus a row auto-mirrored from the
 * catalog below (Service.sbJobTypeKey) — so the picker showed "Installation"
 * twice, "Repair" twice, etc. The actual "Enquiry Types" catalog admins
 * manage today (nouvakadmin/src/pages/Services/catalog/CatalogManager.tsx —
 * "Manage your enquiry types") is the Service-Booking module: backend
 * `SbJobType` / `/api/v1/sb/*`, where `key` is unique by schema, so this
 * source can't repeat an entry the way the legacy collection could. Its
 * `group: 'primary'` entries are CatalogManager's "Booking-based" rows
 * (Service/Repair/Installation, each with real equipment options attached).
 * `group: 'secondary'` entries (AMC, Sell Old Boiler, Expert Advice, Site
 * Visit) are "Request-based" — each has its own dedicated creation flow
 * elsewhere in the admin and its own Enquiry.subType, so they don't belong in
 * this picker.
 *
 * The legacy Service collection still matters for one thing here: when a
 * primary SbJobType has a mirrored Service row (Service.sbJobTypeKey === that
 * job type's key), that row's _id is sent as `serviceId` so the created
 * Enquiry links to a real Service document
 * (Enquiry.details.serviceDetails[0].service_id) — same as the booking flow.
 * When no mirror exists, the Enquiry is still created fine with just
 * `subType`.
 */

// Sentinel for "doesn't match any catalog job type" — still a legitimate
// Enquiry (subType: OTHER, no linked service).
export const OTHER_SERVICE_VALUE = '__other__';

// Real, active, booking-based job types — CatalogManager's "Booking-based"
// rows, the only ones that make sense for a converted lead to dispatch
// against.
export const getPrimaryJobTypes = (jobTypes: SbJobType[]): SbJobType[] =>
  jobTypes
    .filter((jt) => jt.group === 'primary' && jt.isActive !== false)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

// Finds the legacy Service row auto-mirrored from this job type, if any.
export const findMirroredService = (
  services: IService[],
  jobTypeKey: string,
): IService | undefined =>
  services.find(
    (s) => s.isActive === 1 && (s.sbJobTypeKey || '').toLowerCase() === jobTypeKey.toLowerCase(),
  );
